<template>
  <main class="agreement-wrapper">
    <section class="agreement-card">
      <header class="agreement-card__header">
        <img class="agreement-card__logo" :src="clientLogoURL" :alt="`${clientName || 'Client'} logo`">
        <span v-if="clientName && !hideClientName" class="agreement-card__client-name">{{ clientName }}</span>
      </header>
      <div class="agreement-card__accent" aria-hidden="true"></div>

      <div class="agreement-card__content">
        <h1>Non-Disclosure Agreement</h1>
        <p class="agreement-card__intro">Please review and accept this agreement before viewing the project.</p>

        <div v-if="previouslyNotBidding" class="notification is-warning">
          <strong>{{ notBiddingWarning }}</strong>
          <p>Please accept the NDA if you want to change your response.</p>
        </div>

        <template v-if="pendingNda?.nda?.terms || pendingNda?.nda?.content">
          <div class="agreement-content">
            <HTMLText :value="pendingNda.nda.terms || pendingNda.nda.content" />
            <p v-if="pendingNda.nda.updated_at" class="agreement-updated"><i>Updated on {{ formatCultureDate(pendingNda.nda.updated_at) }}</i></p>
          </div>

          <label class="agreement-acceptance">
            <o-checkbox v-model="agreed">
              I have read and agree to this agreement {{ clientName ? `set forth by ${clientName}` : 'set forth by the general contractor' }}.
            </o-checkbox>
          </label>

          <div class="agreement-actions">
            <o-button
              class="agreement-action agreement-action--primary"
              :disabled="!agreed || submitting"
              :loading="submitting"
              @click="handleAcceptNda"
            >
              I Agree
            </o-button>
          </div>
        </template>

        <template v-else>
          <div class="agreement-content">
            <p>The current Non-Disclosure Agreement is unavailable. Please return to the project list and try again.</p>
          </div>
          <div class="agreement-actions">
            <o-button class="agreement-action agreement-action--secondary" @click="returnToProjects">
              Return to projects
            </o-button>
          </div>
        </template>
      </div>
    </section>

    <footer class="agreement-footer">
      <a href="https://pipelinesuite.com/" target="_blank"><img src="https://assets.p-suite.io/powered_by_preconSuiteLogo.svg" width="200" alt="Powered by PreconSuite"></a>
      <a href="https://pipelinesuite.com/privacy-policy" target="_blank">Privacy</a>
      <a href="https://pipelinesuite.com/terms-of-service" target="_blank">Terms</a>
      <a href="https://pipelinesuite.com/contact/" target="_blank">Contact Us</a>
    </footer>
  </main>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import HTMLText from '@/components/HTMLText.vue';
import { useClientStore } from '@/stores/ClientStore';
import { useCompanyStore } from '@/stores/CompanyStore';
import { useProjectStore } from '@/stores/ProjectStore';
import { useBidPackageStore } from '@/stores/BidPackageStore';
import { NotificationError } from '@/internal';
import {formatCultureDate} from '@/use/formatCultureValue';
import BiddingResponseSummaryAPI from '@/api/BiddingResponseSummaryAPI';
import type {BiddingResponseSummary} from '@/models/BiddingResponseSummary';

type PendingNda = {
  projectId: number;
  bidPackageId?: number;
  returnPath?: string;
  nda: {
    id: number;
    terms?: string;
    content?: string;
    client_name?: string;
    updated_at?: string;
  };
};

const router = useRouter();
const projectStore = useProjectStore();
const bidPackageStore = useBidPackageStore();
const { company } = storeToRefs(useCompanyStore());
const { client } = storeToRefs(useClientStore());
const agreed = ref(false);
const submitting = ref(false);
const biddingResponseSummary = ref<BiddingResponseSummary | null>(null);

function readPendingNda(): PendingNda | null {
  try {
    const value = JSON.parse(window.sessionStorage.getItem('pendingNda') || 'null');
    return value?.projectId && value?.nda?.id ? value : null;
  } catch {
    return null;
  }
}

const pendingNda = ref<PendingNda | null>(readPendingNda());
const previouslyNotBidding = computed(() => Boolean(
  biddingResponseSummary.value
  && biddingResponseSummary.value.total_yes < 1
  && biddingResponseSummary.value.total_no > 0,
));
const notBiddingWarning = computed(() => pendingNda.value?.bidPackageId
  ? 'You previously confirmed that you are not bidding on one or more trades.'
  : 'You previously confirmed that you are not bidding on one or more trades or bid packages.');
const clientName = computed(() => pendingNda.value?.nda.client_name || client.value?.name || company.value?.client?.name || '');
const clientLogoURL = computed(() => client.value?.logo_url
  || company.value?.client?.logo_url
  || 'https://assets.p-suite.io/preconsuite-logo.svg');
const hideClientName = computed(() => client.value?.hide_client_name_on_subsportal
  ?? company.value?.client?.hide_client_name_on_subsportal
  ?? false);

onMounted(loadBiddingResponseSummary);

async function loadBiddingResponseSummary() {
  if (!pendingNda.value) {
    return;
  }

  try {
    biddingResponseSummary.value = await new BiddingResponseSummaryAPI().show(
      pendingNda.value.projectId,
      pendingNda.value.bidPackageId,
    );
  } catch {
    // This context is helpful but not required to review or accept the NDA.
    biddingResponseSummary.value = null;
  }
}

function returnToProjects() {
  window.sessionStorage.removeItem('pendingNda');
  router.replace({ name: 'projects/current' });
}

async function handleAcceptNda() {
  if (!pendingNda.value) {
    return;
  }

  submitting.value = true;

  try {
    if (pendingNda.value.bidPackageId) {
      await bidPackageStore.acceptNda(
        pendingNda.value.bidPackageId,
        pendingNda.value.projectId,
        pendingNda.value.nda.id,
      );
    } else {
      await projectStore.acceptNda(pendingNda.value.projectId, pendingNda.value.nda.id);
    }
    const returnPath = pendingNda.value.returnPath || `/projects/${pendingNda.value.projectId}/show`;
    window.sessionStorage.removeItem('pendingNda');
    await router.replace(returnPath);
  } catch (error: any) {
    if (error.errors) {
      NotificationError({}, error);
    } else {
      NotificationError({ message: error });
    }
  } finally {
    submitting.value = false;
  }
}
</script>

<style lang="scss" scoped>
  .agreement-wrapper {
    background: #ffffff;
    color: #252729;
    flex: 1;
    min-height: 100vh;
    padding: 2rem 0;
  }

  .agreement-card {
    background: #fff;
    border-radius: .5rem;
    box-shadow: 0 .5rem 1.5rem rgb(0 0 0 / 10%);
    margin: 0 auto;
    max-width: 68.75rem;
    overflow: hidden;
    width: calc(100% - 2rem);
  }

  .agreement-card__header {
    align-items: center;
    display: flex;
    gap: 1rem;
    padding: 1.5rem 2rem .5rem;
  }

  .agreement-card__logo {
    display: block;
    height: 40px;
    max-width: 250px;
    object-fit: contain;
    object-position: left center;
    width: auto;
  }

  .agreement-card__client-name {
    font-size: 1.25rem;
    font-weight: 600;
  }

  .agreement-card__accent {
    background: var(--theme-menu-background-color);
    border-top: 2px solid var(--theme-border-color);
    box-shadow: 0 2px 2px rgb(0 0 0 / 14%),
      0 3px 1px -2px rgb(0 0 0 / 12%),
      0 1px 5px rgb(0 0 0 / 10%);
    height: 31px;
  }

  .agreement-card__content {
    margin: 0 auto;
    max-width: 53.75rem;
    padding: 2.5rem 2rem 2rem;
  }

  .agreement-card__content h1 {
    font-size: 2rem;
    margin: 0;
  }

  .agreement-card__intro {
    color: #5b6065;
    margin: .5rem 0 1.5rem;
  }

  .agreement-content {
    border: 1px solid #d9dcde;
    border-radius: 4px;
    line-height: 1.55;
    max-height: 22.5rem;
    overflow-y: auto;
    padding: 1.25rem 1.5rem;
  }

  .agreement-acceptance {
    background: #f7f8f8;
    border: 1px solid #d9dcde;
    border-radius: 4px;
    display: block;
    margin-top: 1.5rem;
    padding: 1rem 1.25rem;
  }

  .agreement-actions {
    display: flex;
    gap: .75rem;
    justify-content: flex-end;
    margin-top: 1.25rem;
  }

  .agreement-action {
    align-items: center;
    border-radius: 4px;
    display: inline-flex;
    font-weight: 600;
    justify-content: center;
    min-height: 2.75rem;
    padding: 0 1.25rem;
  }

  .agreement-action--primary {
    background: #29b765;
    border: 1px solid #29b765;
    color: #fff;
  }

  .agreement-action--primary:hover:not([disabled]) {
    background: #229b55;
    border-color: #229b55;
    color: #fff;
  }

  .agreement-action--primary[disabled] {
    background: #e4e7e9;
    border-color: #e4e7e9;
    color: #8a9094;
    cursor: not-allowed;
    opacity: 1;
  }

  .agreement-action--secondary {
    background: #fff;
    border: 1px solid #979ea2;
    color: #3d4145;
  }

  .agreement-footer {
    align-items: center;
    color: #6e6e6e;
    display: flex;
    flex-wrap: wrap;
    font-size: .75rem;
    gap: .75rem 1.5rem;
    justify-content: center;
    margin: 1rem auto 0;
    text-align: center;
  }

  .agreement-footer img {
    display: block;
  }

  @media (max-width: 768px) {
    .agreement-wrapper {
      padding: 0;
    }

    .agreement-card {
      border-radius: 0;
      width: 100%;
    }

    .agreement-card__content {
      padding-left: 1rem;
      padding-right: 1rem;
    }

    .agreement-actions .button {
      flex: 1;
    }
  }
</style>

<style lang="scss">
  @media screen and (max-width: 768px) {
    #app main#main {
      height: 100%;
      margin-top: 0;
      margin-bottom: 0;
    }
  }
</style>
